/**
 * Frueher, nicht-blockierender Prefetch fuer die Anmeldeanzeige.
 *
 * Waermt den Firestore-Cache (Coaches + Appointments) ueber die geteilte
 * window.VivlicoFirebase-db-Instanz, sodass der spaeter bootende Monolith seine
 * identischen Queries per getDocsFromCache() sofort bedient bekommt. Faellt der
 * Cache-Treffer aus, verhaelt sich der Monolith exakt wie bisher (kein Nachteil).
 */
import type { RegistrationStyleData } from './styleTokens.js';
import type { DisplayRoute } from './urlParse.js';

interface FirestoreLike {
    db: unknown;
    collection: (db: unknown, path: string) => unknown;
    query: (...args: unknown[]) => unknown;
    where: (field: string, op: string, value: unknown) => unknown;
    limit: (n: number) => unknown;
    getDocs: (q: unknown) => Promise<{ docs: Array<{ id: string; data: () => Record<string, unknown> }>; empty: boolean }>;
}

export interface EarlyAppointment {
    id?: string | number;
    title?: string;
    region?: string;
    courseImage?: unknown;
    dateString?: string;
    registration?: Record<string, unknown>;
    [key: string]: unknown;
}

export interface PrefetchResult {
    clubDocId: string | null;
    style: RegistrationStyleData | null;
    appointments: EarlyAppointment[];
}

function getFirebase(): FirestoreLike | null {
    const fb = (globalThis as { VivlicoFirebase?: Partial<FirestoreLike> }).VivlicoFirebase;
    if (!fb || !fb.db || typeof fb.collection !== 'function' || typeof fb.query !== 'function'
        || typeof fb.where !== 'function' || typeof fb.getDocs !== 'function' || typeof fb.limit !== 'function') {
        return null;
    }
    return fb as FirestoreLike;
}

export function waitForVivlicoFirebase(maxMs = 8000, intervalMs = 25): Promise<FirestoreLike | null> {
    return new Promise((resolve) => {
        const start = Date.now();
        const tick = (): void => {
            const fb = getFirebase();
            if (fb) {
                resolve(fb);
                return;
            }
            if (Date.now() - start >= maxMs) {
                resolve(null);
                return;
            }
            setTimeout(tick, intervalMs);
        };
        tick();
    });
}

function isEarlyAppointmentVisible(appt: EarlyAppointment): boolean {
    const reg = appt && appt.registration && typeof appt.registration === 'object'
        ? appt.registration
        : null;
    return !reg || reg.enabledVisible !== false;
}

function normalizeCourseId(raw: string): number | string {
    const asNum = /^\d+$/.test(raw) ? parseInt(raw, 10) : NaN;
    return !Number.isNaN(asNum) && String(asNum) === raw ? asNum : raw;
}

async function fetchAppointments(
    fb: FirestoreLike,
    route: DisplayRoute,
    clubDocId: string,
): Promise<EarlyAppointment[]> {
    const appts = fb.collection(fb.db, 'Appointments');
    try {
        let snap;
        if (route.isSingleCourse && route.courseId) {
            const idVal = normalizeCourseId(route.courseId);
            snap = await fb.getDocs(fb.query(
                appts,
                fb.where('clubID', '==', clubDocId),
                fb.where('id', '==', idVal),
                fb.limit(1),
            ));
        } else {
            snap = await fb.getDocs(fb.query(
                appts,
                fb.where('clubID', '==', clubDocId),
                fb.where('registration.enabled', '==', true),
            ));
        }
        if (!snap || snap.empty) return [];
        return snap.docs.map((d) => {
            const data = d.data() || {};
            return { ...data, id: data.id != null ? data.id : d.id } as EarlyAppointment;
        }).filter(isEarlyAppointmentVisible);
    } catch {
        /* Prefetch ist best-effort; Monolith holt bei Bedarf regulaer nach. */
        return [];
    }
}

/**
 * Liest Coaches + Appointments (waermt Cache) und liefert Style + Karten-Daten
 * fuer den fruehen First Paint vor dem Monolithen.
 */
export async function prefetchRegistrationData(route: DisplayRoute): Promise<PrefetchResult> {
    const fb = await waitForVivlicoFirebase();
    if (!fb || !route.clubRegId) {
        return { clubDocId: null, style: null, appointments: [] };
    }
    try {
        const coaches = fb.collection(fb.db, 'Coaches');
        const snap = await fb.getDocs(fb.query(coaches, fb.where('clubRegistrationID', '==', route.clubRegId)));
        if (snap.empty || snap.docs.length === 0) {
            return { clubDocId: null, style: null, appointments: [] };
        }
        const doc = snap.docs[0];
        const data = doc.data() || {};
        const clubReg = (data.clubRegistration as { style?: RegistrationStyleData } | undefined) || undefined;
        const style = clubReg && clubReg.style ? clubReg.style : null;
        const appointments = await fetchAppointments(fb, route, doc.id);
        return { clubDocId: doc.id, style, appointments };
    } catch {
        return { clubDocId: null, style: null, appointments: [] };
    }
}
